import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSubscription } from '../hooks/useSubscription';
import { useSalesforce } from '../hooks/useSalesforce';
import { useInvoices, formatCurrency, formatInvoiceDate, formatInvoiceStatus, getInvoiceStatusColor } from '../hooks/useInvoices';
import { useUsageStats, getHealthGradeColor, getImprovementTrend } from '../hooks/useUsageStats';
import { useUpcomingInvoice, getDiscountAmount, getDiscountDescription, formatCurrency as formatUpcomingCurrency } from '../hooks/useUpcomingInvoice';
import { supabase } from '../services/supabaseClient';
import Navbar from '../components/Navbar';
import { getPlanShortName, getPlanBadgeColor } from '../utils/planHelpers';
import { 
  CreditCardIcon, 
  CalendarIcon, 
  ArrowUpIcon,
  ChartBarIcon,
  BoltIcon,
  DocumentArrowDownIcon,
  CheckCircleIcon,
  SparklesIcon,
  ArrowPathIcon,
  BuildingOfficeIcon,
  PlusIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalculatorIcon
} from '@heroicons/react/24/outline';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { ROICalculatorModal } from '../components/ROICalculatorModal';
import AddEnvironmentModal from '../components/AddEnvironmentModal';

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<'current_billing' | 'last_billing' | 'this_month' | 'last_month' | 'last_30_days'>('last_month');
  const { subscription, loading: subscriptionLoading } = useSubscription();
  const { orgs, isSuperAdmin } = useSalesforce();
  const { invoices: recentInvoices, loading: recentInvoicesLoading } = useInvoices(3); // Show 3 recent invoices
  const { invoices: allInvoices, loading: allInvoicesLoading, hasMore, loadMore } = useInvoices(10); // For full history
  const { stats: usageStats, loading: statsLoading } = useUsageStats(selectedTimePeriod);
  const { upcomingInvoice, loading: upcomingInvoiceLoading } = useUpcomingInvoice();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [updatingPayment, setUpdatingPayment] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cancelImmediately, setCancelImmediately] = useState(false);
  const [showAllInvoices, setShowAllInvoices] = useState(false);
  const [showROIModal, setShowROIModal] = useState(false);
  const [showAddEnvironmentModal, setShowAddEnvironmentModal] = useState(false);
  const [showTimePeriodDropdown, setShowTimePeriodDropdown] = useState(false);
  const [periodLoading, setPeriodLoading] = useState(false);
  const timePeriodDropdownRef = useRef<HTMLDivElement>(null);
  
  // Click outside handler for time period dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (timePeriodDropdownRef.current && !timePeriodDropdownRef.current.contains(event.target as Node)) {
        setShowTimePeriodDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Track loading state when period changes
  useEffect(() => {
    setPeriodLoading(true);
    const timer = setTimeout(() => {
      setPeriodLoading(false);
    }, 2000); // Fallback timeout
    return () => clearTimeout(timer);
  }, [selectedTimePeriod]);
  
  // Clear loading state when stats update
  useEffect(() => {
    if (usageStats) {
      setPeriodLoading(false);
    }
  }, [usageStats]);

  // Debug logging
  useEffect(() => {
    console.log('Subscription data:', subscription);
    console.log('Upcoming invoice:', upcomingInvoice);
    if (usageStats) {
      console.log('Score history:', usageStats.scoreHistory);
      console.log('Issues fixed history:', usageStats.issuesFixedHistory);
    }
  }, [subscription, usageStats, upcomingInvoice]);

  const isStarter = subscription?.subscription_plan === 'starter';
  const isStandard = subscription?.subscription_plan === 'standard';
  const isProfessional = subscription?.subscription_plan === 'professional';
  const isEnterprise = subscription?.subscription_plan === 'enterprise';
  const isCancelled = subscription?.subscription_status === 'canceled' || subscription?.subscription_status === 'cancelled';
  const isCancelledEndOfPeriod = subscription?.subscription_cancel_at && !isCancelled;
  const isAnnual = subscription?.billing_interval === 'year';
  
  // Calculate prices based on billing interval
  // Use upcoming invoice for actual price (includes coupons), fall back to base price
  const getPrice = () => {
    if (isCancelled) return '$0';
    if (isEnterprise) return '$Custom';

    // If we have an upcoming invoice, use the actual amount
    if (upcomingInvoice && !upcomingInvoiceLoading) {
      const actualAmount = upcomingInvoice.amount_due;
      if (isAnnual) {
        return `${formatUpcomingCurrency(actualAmount, upcomingInvoice.currency)}/year`;
      }
      return `${formatUpcomingCurrency(actualAmount, upcomingInvoice.currency)}/month`;
    }

    // Fallback to calculated base price
    let basePrice = 0;
    if (isProfessional) {
      basePrice = 299;
    } else if (isStandard) {
      basePrice = 199;
    } else if (isStarter) {
      basePrice = 99;
    }

    if (isAnnual) {
      const annualPrice = basePrice * 10; // 2 months free on annual
      return `$${annualPrice.toLocaleString()}/year`;
    }
    return `$${basePrice}/month`;
  };

  // Get base price (before discounts) for comparison
  const getBasePrice = () => {
    let basePrice = 0;
    if (isProfessional) {
      basePrice = 299;
    } else if (isStandard) {
      basePrice = 199;
    } else if (isStarter) {
      basePrice = 99;
    }

    if (isAnnual) {
      const annualPrice = basePrice * 10;
      return annualPrice;
    }
    return basePrice;
  };

  // Check if there's a discount applied
  const hasDiscount = upcomingInvoice && getDiscountAmount(upcomingInvoice) > 0;
  const discountDescription = getDiscountDescription(upcomingInvoice);
  
  // Calculate next billing date - for trials use trial_end_date, otherwise show monthly/annual billing
  let nextBillingDate = 'N/A';
  if (isCancelled) {
    nextBillingDate = 'Cancelled';
  } else if (isCancelledEndOfPeriod && subscription?.subscription_cancel_at) {
    nextBillingDate = `Cancels ${new Date(subscription.subscription_cancel_at).toLocaleDateString()}`;
  } else if (subscription?.is_trial && subscription?.trial_end_date) {
    nextBillingDate = new Date(subscription.trial_end_date).toLocaleDateString();
  } else if (subscription?.subscription_status === 'active') {
    // For active subscriptions, calculate based on billing interval
    const nextBilling = new Date();
    if (isAnnual) {
      nextBilling.setFullYear(nextBilling.getFullYear() + 1);
    } else {
      nextBilling.setMonth(nextBilling.getMonth() + 1);
    }
    nextBillingDate = nextBilling.toLocaleDateString();
  }

  const handleUpdatePaymentMethod = async () => {
    setUpdatingPayment(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-customer-portal-session', {
        body: { returnUrl: window.location.href }
      });

      console.log('Portal session response:', { data, error });

      if (error) {
        console.error('Portal session error details:', error);
        throw error;
      }

      if (data?.url) {
        window.location.href = data.url;
      } else {
        console.error('No URL in response:', data);
        throw new Error(data?.error || 'No portal URL received');
      }
    } catch (error: any) {
      console.error('Error opening payment portal:', error);
      const errorMessage = error?.message || error?.error || 'Failed to open payment settings';
      alert(`${errorMessage}. Please try again or contact support.`);
    } finally {
      setUpdatingPayment(false);
    }
  };

  const handleCancelSubscription = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('cancel-subscription', {
        body: { cancelImmediately }
      });

      if (error) throw error;

      alert(cancelImmediately 
        ? 'Your subscription has been canceled immediately.' 
        : 'Your subscription will be canceled at the end of the current billing period.');
      
      setShowCancelModal(false);
      // Refresh the page to show updated status
      window.location.reload();
    } catch (error) {
      console.error('Error canceling subscription:', error);
      alert('Failed to cancel subscription. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  const handleEnvironmentAdded = () => {
    // Refresh the page to show updated subscription
    window.location.reload();
  };

  const handleSaveROISettings = async (values: {
    hourlyRate: number;
    hoursPerNewFlow: number;
    hoursPerFlowUpdate: number;
  }) => {
    try {
      const { error } = await supabase
        .from('accounts')
        .update({
          roi_hourly_rate: values.hourlyRate,
          roi_hours_per_new_flow: values.hoursPerNewFlow,
          roi_hours_per_flow_update: values.hoursPerFlowUpdate
        })
        .eq('owner_id', user?.id);

      if (error) throw error;

      setShowROIModal(false);
      // Refresh stats to reflect new calculations
      window.location.reload();
    } catch (error) {
      console.error('Error saving ROI settings:', error);
      alert('Failed to save ROI settings. Please try again.');
    }
  };

  if (subscriptionLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar onSignOut={signOut} />
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading subscription details...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar onSignOut={signOut} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Subscription & Billing</h1>
          <p className="mt-2 text-gray-600">Manage your plan and see how FlowDocs is helping your team</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Current Plan & Usage */}
          <div className="lg:col-span-2 space-y-6">
            {/* Current Plan Card */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Current Plan</h2>
                <div className="flex items-center gap-2">
                  {/* Show actual subscribed plan (not effective plan which is limited during trial) */}
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPlanBadgeColor(subscription?.subscription_plan)}`}>
                    {getPlanShortName(subscription?.subscription_plan)} Plan
                  </span>
                  {subscription?.is_trial && (
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700">
                      Trial
                    </span>
                  )}
                  {isCancelled && (
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700">
                      Cancelled
                    </span>
                  )}
                  {isCancelledEndOfPeriod && (
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-700">
                      Cancelling
                    </span>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <p className="text-sm text-gray-500">
                    {isCancelled ? 'Cost' : subscription?.is_trial ? 'Price after trial' : isAnnual ? 'Annual Cost' : 'Monthly Cost'}
                  </p>
                  <div className="flex items-baseline gap-2">
                    <p className="text-2xl font-bold text-gray-900">
                      {getPrice()}
                    </p>
                    {hasDiscount && !isCancelled && (
                      <span className="text-sm font-medium text-green-600">
                        {discountDescription}
                      </span>
                    )}
                  </div>
                  {hasDiscount && !isCancelled && !isEnterprise && (
                    <p className="text-xs text-gray-500 line-through mt-1">
                      ${getBasePrice()}{isAnnual ? '/year' : '/month'}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-500">
                    {subscription?.is_trial ? 'Trial ends' : 'Next Billing Date'}
                  </p>
                  <p className="text-lg font-medium text-gray-900">{nextBillingDate}</p>
                </div>
              </div>

              {subscription?.is_trial && !isCancelled && !isCancelledEndOfPeriod && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-700">
                    You're currently on a 7-day trial. Your card will be charged when the trial ends.
                  </p>
                </div>
              )}

              {isCancelled && (
                <div className="mb-4 p-3 bg-red-50 rounded-lg">
                  <p className="text-sm text-red-700">
                    Your subscription has been cancelled. You will lose access to premium features.
                  </p>
                </div>
              )}

              {isCancelledEndOfPeriod && (
                <div className="mb-4 p-3 bg-orange-50 rounded-lg">
                  <p className="text-sm text-orange-700">
                    Your subscription will be cancelled on {new Date(subscription.subscription_cancel_at).toLocaleDateString()}. You'll retain access until then.
                  </p>
                </div>
              )}

              {!isCancelled && (
                <div className="flex gap-3">
                  <button 
                    onClick={handleUpdatePaymentMethod}
                    disabled={updatingPayment}
                    className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
                  >
                    {updatingPayment ? 'Loading...' : 'Update Payment Method'}
                  </button>
                  <span className="text-gray-300">•</span>
                  <button 
                    onClick={() => setShowCancelModal(true)}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    Cancel Subscription
                  </button>
                </div>
              )}
            </div>

            {/* Results Section */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Results</h2>
              
              {statsLoading ? (
                <div className="flex items-center justify-center h-48">
                  <div className="text-gray-500">Loading statistics...</div>
                </div>
              ) : usageStats ? (
                <div className="space-y-6">
                  {/* Top Row - Total Savings and Key Metrics */}
                  <div className="grid grid-cols-3 gap-6 h-40">
                    {/* Total Savings */}
                    <div className="bg-green-50 rounded-lg p-6 border border-green-200 relative flex flex-col justify-end">
                      {isSuperAdmin && (
                        <button
                          onClick={() => setShowROIModal(true)}
                          className="absolute top-4 right-4 p-1 text-green-700 hover:text-green-900 hover:bg-green-100 rounded transition-colors"
                          title="Customize ROI calculations"
                        >
                          <CalculatorIcon className="h-5 w-5" />
                        </button>
                      )}
                      <div>
                        <p className="text-4xl font-bold text-green-900">
                          ${usageStats.totalSavings.toLocaleString()}
                        </p>
                        <p className="text-sm font-medium text-green-700 mt-1">Total Savings</p>
                        <p className="text-xs text-green-600 mt-1">
                          {usageStats.totalHoursSaved.toFixed(1)} hours at ${usageStats.hourlyRate || 85}/hr
                          <br />
                          AFTER subscription fees
                        </p>
                      </div>
                    </div>
                    
                    {/* Savings by Time Period */}
                    <div className="bg-blue-50 rounded-lg p-6 border border-blue-200 flex flex-col justify-end relative">
                      {/* Calendar Icon Dropdown */}
                      <div ref={timePeriodDropdownRef} className="absolute top-4 right-4">
                        <button
                          onClick={() => setShowTimePeriodDropdown(!showTimePeriodDropdown)}
                          className="p-1 text-blue-700 hover:text-blue-900 hover:bg-blue-100 rounded transition-colors"
                          title="Change time period"
                        >
                          <CalendarIcon className="h-5 w-5" />
                        </button>
                        
                        {showTimePeriodDropdown && (
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                            <button
                              onClick={() => {
                                setSelectedTimePeriod('current_billing');
                                setShowTimePeriodDropdown(false);
                              }}
                              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                                selectedTimePeriod === 'current_billing' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                              }`}
                            >
                              Current Billing Cycle
                            </button>
                            <button
                              onClick={() => {
                                setSelectedTimePeriod('last_billing');
                                setShowTimePeriodDropdown(false);
                              }}
                              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                                selectedTimePeriod === 'last_billing' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                              }`}
                            >
                              Last Billing Cycle
                            </button>
                            <button
                              onClick={() => {
                                setSelectedTimePeriod('this_month');
                                setShowTimePeriodDropdown(false);
                              }}
                              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                                selectedTimePeriod === 'this_month' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                              }`}
                            >
                              This Month
                            </button>
                            <button
                              onClick={() => {
                                setSelectedTimePeriod('last_month');
                                setShowTimePeriodDropdown(false);
                              }}
                              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                                selectedTimePeriod === 'last_month' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                              }`}
                            >
                              Last Month
                            </button>
                            <button
                              onClick={() => {
                                setSelectedTimePeriod('last_30_days');
                                setShowTimePeriodDropdown(false);
                              }}
                              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 rounded-b-lg ${
                                selectedTimePeriod === 'last_30_days' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                              }`}
                            >
                              Last 30 Days
                            </button>
                          </div>
                        )}
                      </div>
                      
                      <div>
                        {periodLoading ? (
                          <>
                            <div className="h-10 flex items-center">
                              <div className="animate-pulse bg-blue-200 h-8 w-32 rounded"></div>
                            </div>
                            <p className="text-sm font-medium text-blue-700 mt-1">
                              Savings {selectedTimePeriod === 'current_billing' ? 'This Billing Cycle' : 
                                       selectedTimePeriod === 'last_billing' ? 'Last Billing Cycle' :
                                       selectedTimePeriod === 'this_month' ? 'This Month' : 
                                       selectedTimePeriod === 'last_30_days' ? 'Last 30 Days' : 'Last Month'}
                            </p>
                            <div className="mt-1">
                              <div className="animate-pulse bg-blue-100 h-4 w-24 rounded mb-1"></div>
                              <div className="animate-pulse bg-blue-100 h-4 w-16 rounded"></div>
                            </div>
                          </>
                        ) : (
                          <>
                            <p className="text-4xl font-bold text-blue-900">
                              {usageStats.lastMonthSavings === undefined || usageStats.lastMonthSavings === 0 ? '$TBD' : `$${usageStats.lastMonthSavings.toLocaleString()}`}
                            </p>
                            <p className="text-sm font-medium text-blue-700 mt-1">
                              Savings {selectedTimePeriod === 'current_billing' ? 'This Billing Cycle' : 
                                       selectedTimePeriod === 'last_billing' ? 'Last Billing Cycle' :
                                       selectedTimePeriod === 'this_month' ? 'This Month' : 
                                       selectedTimePeriod === 'last_30_days' ? 'Last 30 Days' : 'Last Month'}
                            </p>
                            <p className="text-xs text-blue-600 mt-1">
                              {usageStats.lastMonthNewFlows || 0} new + {usageStats.lastMonthUpdatedFlows || 0} updated
                              <br />
                              {(usageStats.lastMonthHoursSaved || 0).toFixed(1)} hours
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Flows Summary */}
                    <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 flex flex-col justify-end">
                      <div>
                        {/* Row 1 - Flows Analyzed */}
                        <div className="grid grid-cols-3 gap-2 items-end">
                          <div className="col-span-1 text-right">
                            <p className="text-2xl font-bold text-gray-900 leading-none">{usageStats.flowsAnalyzed}</p>
                          </div>
                          <div className="col-span-1">
                            <p className="text-[0.65rem] leading-none text-gray-500">Flows Analyzed</p>
                          </div>
                          <div className="col-span-1 text-right">
                            <p className="text-[0.65rem] leading-none text-gray-500">{(usageStats.flowsAnalyzed * usageStats.hoursPerFlowAnalyzed).toFixed(1)} hrs</p>
                          </div>
                        </div>
                        
                        {/* Row 2 - Flows Updated */}
                        <div className="grid grid-cols-3 gap-2 items-end mt-[6px]">
                          <div className="col-span-1 text-right">
                            <p className="text-2xl font-bold text-gray-900 leading-none">{usageStats.flowsUpdated || 0}</p>
                          </div>
                          <div className="col-span-1">
                            <p className="text-[0.65rem] leading-none text-gray-500">Flows Updated</p>
                          </div>
                          <div className="col-span-1 text-right">
                            <p className="text-[0.65rem] leading-none text-gray-500">{((usageStats.flowsUpdated || 0) * usageStats.hoursPerFlowUpdated).toFixed(1)} hrs</p>
                          </div>
                        </div>
                        
                        {/* Row 3 - Flow Activity (moved to bottom) */}
                        <div className="mt-[12px] pt-2 border-t border-gray-200">
                          <div className="grid grid-cols-3 gap-2 items-end">
                            <div className="col-span-2">
                              <p className="text-sm font-medium text-gray-700 leading-none">Flow Activity</p>
                            </div>
                            <div className="col-span-1 text-right">
                              <p className="text-[0.65rem] leading-none text-gray-500">{(usageStats.flowsAnalyzed * usageStats.hoursPerFlowAnalyzed + (usageStats.flowsUpdated || 0) * usageStats.hoursPerFlowUpdated).toFixed(1)} total</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Second Row - 2x2 Grid */}
                  <div className="grid grid-cols-2 gap-6">
                    
                    {/* Issues Fixed */}
                    <div className="bg-gray-50 rounded-lg p-6">
                      <div className="flex items-center gap-2">
                        <p className="text-4xl font-bold text-gray-900">{usageStats.issuesResolved}</p>
                        {usageStats.issueReductionPercentage > 0 && (
                          <span className="text-sm font-medium text-red-600">
                            {usageStats.issueReductionPercentage}% reduction!
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-medium text-gray-700 mt-1">Issues fixed</p>
                      <div className="mt-3 h-12">
                        {usageStats.issuesFixedHistory && usageStats.issuesFixedHistory.length > 1 ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={usageStats.issuesFixedHistory} margin={{ top: 0, right: 0, bottom: 1, left: 0 }}>
                              <ReferenceLine y={0} stroke="#000" strokeWidth={1} />
                              <Line 
                                type="monotone" 
                                dataKey="count" 
                                stroke="#dc2626" 
                                strokeWidth={2}
                                dot={false}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="flex items-center justify-center h-full">
                            <div className="w-2 h-2 bg-red-500 rounded-full" />
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Average Score */}
                    <div className="bg-gray-50 rounded-lg p-6">
                      <div className="flex items-center gap-2">
                        <p className="text-4xl font-bold text-gray-900">{usageStats.averageScore.toFixed(1)}%</p>
                        {usageStats.improvementPercentage > 0 && (
                          <span className="text-sm font-medium text-green-600">
                            {usageStats.improvementPercentage}% improvement!
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-medium text-gray-700 mt-1">Average Score</p>
                      <div className="mt-3 h-12">
                        {usageStats.scoreHistory && usageStats.scoreHistory.length > 1 ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={usageStats.scoreHistory} margin={{ top: 0, right: 0, bottom: 1, left: 0 }}>
                              <ReferenceLine y={0} stroke="#000" strokeWidth={1} />
                              <Line 
                                type="monotone" 
                                dataKey="score" 
                                stroke="#16a34a" 
                                strokeWidth={2}
                                dot={false}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="flex items-center justify-center h-full">
                            <div className="w-2 h-2 bg-green-500 rounded-full" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Platform Usage Comparison */}
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-lg font-semibold text-gray-900">Platform Usage</p>
                      <p className="text-sm text-gray-600">{usageStats.platformUsage?.period || 'Since Jul 26'}</p>
                    </div>
                    {usageStats.platformUsage ? (
                      <div className="space-y-3">
                        {/* Calculate max value for proper scaling */}
                        {(() => {
                          const maxDays = Math.max(usageStats.platformUsage.salesforceDays, usageStats.platformUsage.flowDocsDays, 1);
                          return (
                            <>
                              {/* Salesforce Bar */}
                              <div>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm font-medium text-gray-700">Salesforce</span>
                                  <span className="text-sm text-gray-600">{usageStats.platformUsage.salesforceDays} days</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="bg-blue-500 h-2 rounded-full" 
                                    style={{ width: `${(usageStats.platformUsage.salesforceDays / maxDays) * 100}%` }}
                                  />
                                </div>
                              </div>
                              
                              {/* FlowDocs Bar */}
                              <div>
                                <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-700">FlowDocs</span>
                            <span className="text-sm text-gray-600">{usageStats.platformUsage.flowDocsDays} days</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-green-500 h-2 rounded-full" 
                              style={{ width: `${(usageStats.platformUsage.flowDocsDays / maxDays) * 100}%` }}
                            />
                          </div>
                                </div>
                                
                                {/* Engagement Rate */}
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-700">Engagement Rate</span>
                                    <span className={`text-lg font-bold ${
                                      usageStats.platformUsage.engagementRate >= 100 ? 'text-purple-600' :
                                      usageStats.platformUsage.engagementRate >= 80 ? 'text-green-600' :
                                      usageStats.platformUsage.engagementRate >= 50 ? 'text-yellow-600' :
                                      'text-red-600'
                                    }`}>
                                      {usageStats.platformUsage.engagementRate}%
                                    </span>
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {usageStats.platformUsage.engagementRate > 100 
                                      ? `Power user! Using FlowDocs ${usageStats.platformUsage.engagementRate}% as much as Salesforce`
                                      : `Using FlowDocs ${usageStats.platformUsage.engagementRate}% as often as Salesforce`
                                    }
                                  </p>
                                </div>
                              </>
                            )
                          })()}
                        </div>
                    ) : (
                      <div className="h-32 bg-gray-100 rounded flex items-center justify-center">
                        <p className="text-xs text-gray-400">No platform usage data available</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">Connect your first org to see your impact statistics</p>
                  <button 
                    onClick={() => navigate('/orgs')}
                    className="mt-4 text-sm text-blue-600 hover:text-blue-800"
                  >
                    Connect an Organization →
                  </button>
                </div>
              )}
            </div>

            {/* Upgrade Section for Starter and Standard Users */}
            {(isStarter || isStandard) && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow p-6 border border-blue-200">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Unlock More with Professional
                    </h3>
                    <ul className="space-y-2 mb-4">
                      <li className="flex items-center text-sm text-gray-700">
                        <SparklesIcon className="w-5 h-5 text-blue-600 mr-2" />
                        Export flow documentation as PDFs
                      </li>
                      <li className="flex items-center text-sm text-gray-700">
                        <ArrowPathIcon className="w-5 h-5 text-blue-600 mr-2" />
                        On-demand flow syncing (not just daily)
                      </li>
                      <li className="flex items-center text-sm text-gray-700">
                        <BuildingOfficeIcon className="w-5 h-5 text-blue-600 mr-2" />
                        Connect unlimited orgs (with add-on)
                      </li>
                    </ul>
                    <button
                      onClick={() => navigate('/plans')}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      <ArrowUpIcon className="w-4 h-4 mr-2" />
                      Upgrade to Professional
                    </button>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-gray-900">$299</p>
                    <p className="text-sm text-gray-500">/month</p>
                    <p className="text-xs text-green-600 mt-1">+$150 in value</p>
                  </div>
                </div>
              </div>
            )}

            {/* Add-ons Section for Professional Users */}
            {isProfessional && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Expand Your Coverage
                </h3>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <BuildingOfficeIcon className="w-8 h-8 text-gray-400 mr-3" />
                    <div>
                      <p className="font-medium text-gray-900">Additional Environments</p>
                      <p className="text-sm text-gray-500">
                        Currently using 1 of 1 included environment
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-gray-900">$99/environment</p>
                    <button
                      onClick={() => setShowAddEnvironmentModal(true)}
                      className="mt-1 inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                    >
                      <PlusIcon className="w-4 h-4 mr-1" />
                      Add Environment
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Billing & Actions */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={() => handleUpdatePaymentMethod()}
                  className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center">
                    <CreditCardIcon className="w-5 h-5 text-gray-400 mr-3" />
                    <span className="text-sm font-medium text-gray-700">Update Payment Method</span>
                  </div>
                  <span className="text-gray-400">→</span>
                </button>

                {isProfessional && (
                  <button
                    onClick={() => setShowAddEnvironmentModal(true)}
                    className="w-full flex items-center justify-between p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                  >
                    <div className="flex items-center">
                      <PlusIcon className="w-5 h-5 text-green-600 mr-3" />
                      <span className="text-sm font-medium text-green-700">Add More Environments</span>
                    </div>
                    <span className="text-green-600">→</span>
                  </button>
                )}

                {!isProfessional && !subscription?.subscription_plan?.includes('enterprise') && (
                  <button
                    onClick={() => navigate('/plans')}
                    className="w-full flex items-center justify-between p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <div className="flex items-center">
                      <ArrowUpIcon className="w-5 h-5 text-blue-600 mr-3" />
                      <span className="text-sm font-medium text-blue-700">Upgrade Plan</span>
                    </div>
                    <span className="text-blue-600">→</span>
                  </button>
                )}
                
                <button 
                  onClick={() => setShowCancelModal(true)}
                  className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center">
                    <CalendarIcon className="w-5 h-5 text-gray-400 mr-3" />
                    <span className="text-sm font-medium text-gray-700">Cancel Subscription</span>
                  </div>
                  <span className="text-gray-400">→</span>
                </button>
              </div>
            </div>

            {/* Recent Invoices */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Invoices</h3>
              {recentInvoicesLoading ? (
                <div className="py-4 text-center text-sm text-gray-500">
                  Loading invoices...
                </div>
              ) : recentInvoices.length === 0 ? (
                <div className="py-4 text-center">
                  <DocumentArrowDownIcon className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">
                    {subscription?.is_trial 
                      ? "No invoices yet. Your first invoice will be generated when your trial ends."
                      : "No invoices yet"}
                  </p>
                  {subscription?.is_trial && subscription?.trial_end_date && (
                    <p className="text-xs text-gray-400 mt-1">
                      Trial ends on {new Date(subscription.trial_end_date).toLocaleDateString()}
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    {recentInvoices.map((invoice) => (
                      <div key={invoice.id} className="flex items-center justify-between py-2">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {formatInvoiceDate(invoice.created)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {invoice.number || `#${invoice.id.slice(-8).toUpperCase()}`}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">
                            {formatCurrency(invoice.amount_paid || invoice.amount_due, invoice.currency)}
                          </p>
                          <p className={`text-xs ${invoice.paid ? 'text-green-600' : 'text-yellow-600'}`}>
                            {invoice.paid ? 'Paid' : 'Pending'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  {allInvoices.length > 3 && (
                    <button 
                      onClick={() => setShowAllInvoices(true)}
                      className="mt-4 text-sm text-blue-600 hover:text-blue-800"
                    >
                      View all invoices →
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Support */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Need Help?</h3>
              <p className="text-sm text-gray-600 mb-3">
                Questions about billing or need to make changes?
              </p>
              <button className="text-sm font-medium text-blue-600 hover:text-blue-800">
                Contact Support →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Full Invoice History Modal */}
      {showAllInvoices && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-4xl w-full my-8">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Invoice History</h3>
                <button
                  onClick={() => setShowAllInvoices(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="max-h-[70vh] overflow-y-auto">
              {allInvoicesLoading && !allInvoices.length ? (
                <div className="px-6 py-8 text-center">
                  <p className="text-sm text-gray-500">Loading invoices...</p>
                </div>
              ) : allInvoices.length === 0 ? (
                <div className="px-6 py-8 text-center">
                  <p className="text-sm text-gray-500">No invoices yet</p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Invoice
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {allInvoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatInvoiceDate(invoice.created)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {invoice.number || `#${invoice.id.slice(-8).toUpperCase()}`}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getInvoiceStatusColor(invoice.status)}`}>
                            {formatInvoiceStatus(invoice.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(invoice.amount_paid || invoice.amount_due, invoice.currency)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {invoice.invoice_pdf && (
                            <a
                              href={invoice.invoice_pdf}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-900 inline-flex items-center"
                            >
                              <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                              Download
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              
              {hasMore && (
                <div className="px-6 py-3 bg-gray-50 text-center border-t border-gray-200">
                  <button
                    onClick={loadMore}
                    disabled={allInvoicesLoading}
                    className="text-sm text-blue-600 hover:text-blue-900 font-medium disabled:opacity-50"
                  >
                    {allInvoicesLoading ? 'Loading...' : 'Load more invoices'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Cancel Subscription</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to cancel your subscription? 
              {subscription?.is_trial && ' You are currently in a free trial period.'}
            </p>
            
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={cancelImmediately}
                  onChange={(e) => setCancelImmediately(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Cancel immediately (lose access now)
                </span>
              </label>
              {!cancelImmediately && (
                <p className="mt-2 text-xs text-gray-500">
                  If unchecked, you'll keep access until the end of your current billing period.
                </p>
              )}
            </div>
            
            <div className="flex gap-3 justify-end">
              <button 
                onClick={() => setShowCancelModal(false)}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Keep Subscription
              </button>
              <button 
                onClick={handleCancelSubscription}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {loading ? 'Canceling...' : 'Confirm Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ROI Calculator Modal */}
      <ROICalculatorModal
        isOpen={showROIModal}
        onClose={() => setShowROIModal(false)}
        currentValues={{
          hourlyRate: usageStats?.hourlyRate || 85,
          hoursPerNewFlow: usageStats?.hoursPerFlowAnalyzed || 1.5,
          hoursPerFlowUpdate: usageStats?.hoursPerFlowUpdated || 0.75
        }}
        onSave={handleSaveROISettings}
      />

      {/* Add Environment Modal */}
      <AddEnvironmentModal
        isOpen={showAddEnvironmentModal}
        onClose={() => setShowAddEnvironmentModal(false)}
        onSuccess={handleEnvironmentAdded}
        currentEnvironmentCount={1}
        billingInterval={isAnnual ? 'year' : 'month'}
      />
    </div>
  );
}